/* ==========================================================================
   DIAMOND SEARCH ENGINE

   Downloads the index once, then filters entirely in the browser. No request
   per filter change, so the UI is instant — faster than the reference sites,
   which round-trip every keystroke.

   The index is a compact { fields, rows } shape (rows are arrays, not objects)
   because it is downloaded whole. We expand it to objects once on load.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-diamond-search]');
  if (!root) return;

  var PAGE = 20;
  var MANUAL_MORE = document.body.classList.contains('tmpl-index');
  var COLOURS = ['K', 'J', 'I', 'H', 'G', 'F', 'E', 'D'];
  var CLARITY = ['SI2', 'SI1', 'VS2', 'VS1', 'VVS2', 'VVS1', 'IF', 'FL'];
  var CUTS = ['Good', 'Very Good', 'Excellent', 'Ideal'];
  // Polish, symmetry and fluorescence are ORDERED grades, so they belong on the
  // same slider as the four Cs — as multi-select chips they read as unrelated.
  var GRADE_CODES = ['G', 'VG', 'EX'];
  var GRADE_LABELS = ['Good', 'Very Good', 'Excellent'];
  var FLUOR = ['None', 'Faint', 'Medium', 'Strong'];

  /* A fancy colour is a HUE plus an INTENSITY — "Fancy Vivid Pink" — and neither
     belongs on the D–K scale, which is why the Fancy tab hides it. The feed
     writes the two joined into one string, so we split them back apart here.

     Hue is the LAST colour word: in "Fancy Intense Brownish Yellow" the stone is
     a yellow with a brown cast, and a shopper hunting yellows must find it. The
     -ish/-y forms are only ever modifiers, but they are mapped anyway so a new
     one arriving alone still lands somewhere. */
  var HUE_MAP = {
    pink: 'Pink', pinkish: 'Pink',
    yellow: 'Yellow', yellowish: 'Yellow',
    blue: 'Blue', bluish: 'Blue',
    green: 'Green', greenish: 'Green',
    orange: 'Orange', orangy: 'Orange', orangish: 'Orange',
    brown: 'Brown', brownish: 'Brown',
    gray: 'Gray', grey: 'Gray', grayish: 'Gray', greyish: 'Gray',
    black: 'Black', red: 'Red', reddish: 'Red',
    purple: 'Purple', purplish: 'Purple', violet: 'Violet'
  };
  // Strongest first, and longest-prefix first — 'Fancy Light' must be tested
  // before 'Fancy', and 'Very Light' before 'Light', or both match wrongly.
  var INTENSITIES = ['Fancy Vivid', 'Fancy Intense', 'Fancy Deep', 'Fancy Dark',
                     'Fancy Light', 'Fancy', 'Very Light', 'Light', 'Faint'];
  var HUE_ORDER = ['Pink', 'Yellow', 'Blue', 'Green', 'Orange', 'Brown',
                   'Gray', 'Black', 'Red', 'Purple', 'Violet'];

  /* The feed is hand-keyed and carries typos — "Fancyintense  Yellow" with the
     space missing, "F Vivid Pink" with the word truncated. Both are real stones
     that would otherwise fall into the wrong bucket. */
  function fancyHue(raw) {
    var words = String(raw || '').toLowerCase().replace(/[^a-z ]/g, ' ').split(/\s+/);
    for (var i = words.length - 1; i >= 0; i--) {
      if (HUE_MAP[words[i]]) return HUE_MAP[words[i]];
    }
    return '';
  }

  function fancyIntensity(raw) {
    var s = String(raw || '').replace(/fancy(?=[a-z])/i, 'Fancy ')
                             .replace(/\s+/g, ' ').trim().toLowerCase();
    for (var i = 0; i < INTENSITIES.length; i++) {
      if (s.indexOf(INTENSITIES[i].toLowerCase()) === 0) return INTENSITIES[i];
    }
    // No leading grade word, but one may still be in there ("F Vivid Pink").
    var mods = ['Vivid', 'Intense', 'Deep', 'Dark'];
    for (var j = 0; j < mods.length; j++) {
      if (s.indexOf(mods[j].toLowerCase()) > -1) return 'Fancy ' + mods[j];
    }
    return 'Fancy';   // plain "Pink" is a Fancy Pink
  }

  var stones = [];
  var matches = [];
  var shown = PAGE;
  // Ring Studio embeds this same search as its step 2. In that mode the card
  // action picks the stone for the ring instead of adding it to the cart.
  var SELECT_MODE = root.hasAttribute('data-select-mode');
  // Locale-aware product root — /products/ on the primary market, /fr/products/
  // and so on elsewhere. Shopify publishes it; fall back for the offline preview.
  var PRODUCT_URL = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'products/';
  // Ring Studio narrows the search to what the chosen head physically holds.
  var pendingConstraint = null;

  var state = {
    // growth and colour are independent filters — a stone can be lab grown AND
    // fancy pink. Do not collapse these back into one "type".
    growth: root.getAttribute('data-growth') || '',      // '' | 'Lab Grown' | 'Natural'
    colourTab: root.getAttribute('data-colour-tab') || 'white',
    shapes: [],
    caratMin: null, caratMax: null,
    priceMin: null, priceMax: null,
    colour: [0, 7],
    // Fancy-tab only. Empty means "every fancy colour", exactly like shapes.
    hues: [], intensities: [],
    clarity: [0, 7],
    cut: [0, 3],
    polish: [0, 2], symmetry: [0, 2], fluor: [0, 3],
    tableMin: null, tableMax: null,
    ratioMin: null, ratioMax: null,
    lab: [],
    sort: 'price-asc',
    view: 'grid'
  };

  var bounds = { carat: [0, 10], price: [0, 100000], table: [50, 70], ratio: [1, 2] };

  /* ---------- helpers ---------- */
  function $(sel) { return root.querySelector(sel); }
  function $$(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function money(n) {
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* ---------- load ---------- */
  function load() {
    // The offline preview inlines the index, because a page opened as a data:
    // or file: URL cannot fetch a sibling file.
    if (window.__DS_INDEX__) { ingest(window.__DS_INDEX__); return; }

    var url = root.getAttribute('data-index');
    fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(ingest)
      .catch(function (err) {
        $('[data-results]').innerHTML =
          '<div class="ds-empty"><h3>Diamonds could not be loaded</h3>' +
          '<p class="muted">' + esc(err.message) + '</p></div>';
      });
  }

  function ingest(data) {
    var f = data.fields;
    stones = data.rows.map(function (row) {
      var o = {};
      for (var i = 0; i < f.length; i++) o[f[i]] = row[i];
      o._col = COLOURS.indexOf(o.color);          // -1 for fancy colours
      if (o._col === -1) {
        o._hue = fancyHue(o.color);
        o._int = fancyIntensity(o.color);
      }
      o._cla = CLARITY.indexOf(o.clarity);
      o._cut = CUTS.indexOf(o.cut);               // -1 when the shape has no cut grade
      return o;
    });
    computeBounds();
    readUrl();
    buildControls();
    if (pendingConstraint) { applyConstraint(pendingConstraint); pendingConstraint = null; }
    else apply();
  }

  /* Outside callers (Ring Studio) constrain the search rather than filtering
     the results themselves — otherwise the visible count would contradict the
     list. Arrives before load on a slow connection, so it queues. */
  root.addEventListener('diamond:constrain', function (e) {
    if (!stones.length) { pendingConstraint = e.detail || {}; return; }
    applyConstraint(e.detail || {});
  });

  function applyConstraint(c) {
    if (c.shapes && c.shapes.length) {
      state.shapes = c.shapes.slice();
    } else if (c.shapes) {
      state.shapes = [];
    }
    if (typeof c.caratMin === 'number') state.caratMin = Math.max(bounds.carat[0], c.caratMin);
    if (typeof c.caratMax === 'number') state.caratMax = Math.min(bounds.carat[1], c.caratMax);

    $$('[data-shape]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(state.shapes.indexOf(b.getAttribute('data-shape')) > -1));
    });
    var w = $('[data-range-carat]');
    if (w) {
      w.querySelector('[data-lo]').value = state.caratMin;
      w.querySelector('[data-hi]').value = state.caratMax;
      w.querySelector('[data-lo]').dispatchEvent(new Event('input'));
    }
    paintShapes();
    apply();
  }

  function computeBounds() {
    var pool = state.growth ? stones.filter(function (s) { return s.growth === state.growth; }) : stones;
    if (!pool.length) pool = stones;
    var cts = pool.map(function (s) { return s.carat; });
    var prs = pool.map(function (s) { return s.price; });
    bounds.carat = [Math.floor(Math.min.apply(null, cts) * 10) / 10,
                    Math.ceil(Math.max.apply(null, cts))];
    bounds.price = [0, Math.ceil(Math.max.apply(null, prs) / 1000) * 1000];
    if (state.caratMin === null) state.caratMin = bounds.carat[0];
    if (state.caratMax === null) state.caratMax = bounds.carat[1];
    if (state.priceMin === null) state.priceMin = bounds.price[0];
    if (state.priceMax === null) state.priceMax = bounds.price[1];

    var tbl = pool.map(function (s) { return s.table_pct; });
    var rat = pool.map(function (s) { return s.ratio; });
    bounds.table = [Math.floor(Math.min.apply(null, tbl)), Math.ceil(Math.max.apply(null, tbl))];
    bounds.ratio = [Math.floor(Math.min.apply(null, rat) * 10) / 10,
                    Math.ceil(Math.max.apply(null, rat) * 10) / 10];
    if (state.tableMin === null) state.tableMin = bounds.table[0];
    if (state.tableMax === null) state.tableMax = bounds.table[1];
    if (state.ratioMin === null) state.ratioMin = bounds.ratio[0];
    if (state.ratioMax === null) state.ratioMax = bounds.ratio[1];
  }

  /* ---------- URL state (shareable filters) ---------- */
  function readUrl() {
    var p = new URLSearchParams(location.search);
    if (p.get('shape')) {
      // The home shape strip links through as ?shape=square-radiant, so hyphens
      // must become spaces before title-casing or multi-word shapes never match.
      state.shapes = p.get('shape').split(',').map(titleCase);
    }
    if (p.get('cmin')) state.caratMin = +p.get('cmin');
    if (p.get('cmax')) state.caratMax = +p.get('cmax');
    if (p.get('pmin')) state.priceMin = +p.get('pmin');
    if (p.get('pmax')) state.priceMax = +p.get('pmax');
    if (p.get('sort')) state.sort = p.get('sort');
    // Which colour tab, and the fancy picks — otherwise a link to "Fancy Vivid
    // Pink" reopens on whatever tab the section was configured with.
    if (p.get('colour')) state.colourTab = p.get('colour');
    if (p.get('hue')) state.hues = p.get('hue').split(',').map(titleCase);
    if (p.get('intensity')) state.intensities = p.get('intensity').split(',').map(titleCase);
  }

  function titleCase(s) {
    return s.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function slugList(arr) {
    return arr.join(',').toLowerCase().replace(/ /g, '-');
  }

  function writeUrl() {
    var p = new URLSearchParams();
    if (state.shapes.length) p.set('shape', slugList(state.shapes));
    if (state.colourTab !== (root.getAttribute('data-colour-tab') || 'white')) {
      p.set('colour', state.colourTab);
    }
    if (state.colourTab === 'fancy') {
      if (state.hues.length) p.set('hue', slugList(state.hues));
      if (state.intensities.length) p.set('intensity', slugList(state.intensities));
    }
    if (state.caratMin > bounds.carat[0]) p.set('cmin', state.caratMin);
    if (state.caratMax < bounds.carat[1]) p.set('cmax', state.caratMax);
    if (state.priceMin > bounds.price[0]) p.set('pmin', state.priceMin);
    if (state.priceMax < bounds.price[1]) p.set('pmax', state.priceMax);
    if (state.sort !== 'price-asc') p.set('sort', state.sort);
    var q = p.toString();
    // replaceState throws on data:/sandboxed origins (the offline preview hits
    // this). Syncing the URL is a nicety — it must never break the search.
    try {
      history.replaceState(null, '', q ? '?' + q : location.pathname);
    } catch (e) { /* no-op */ }
  }

  /* Does a stone's grade index pass an ordered slider?
     idx  — position on the scale, or -1 when the stone was never graded
     range — [lo, hi] the shopper has selected
     size  — length of the scale, so we can tell "full range" from "narrowed"

     An ungraded stone passes while the slider spans everything, and drops out
     as soon as the shopper narrows it — at that point they have asked for a
     specific grade, and "no grade" is not it. */
  function inGrade(idx, range, size) {
    if (idx === -1) return range[0] === 0 && range[1] === size - 1;
    return idx >= range[0] && idx <= range[1];
  }

  /* ---------- filtering ---------- */
  function apply() {
    var fancy = state.colourTab === 'fancy';
    var showAll = state.colourTab === 'all';

    matches = stones.filter(function (s) {
      if (state.growth && s.growth !== state.growth) return false;
      if (state.shapes.length && state.shapes.indexOf(s.shape) === -1) return false;
      if (s.carat < state.caratMin || s.carat > state.caratMax) return false;
      if (s.price < state.priceMin || s.price > state.priceMax) return false;

      // Fancy stones have no D–K grade, so the white scale must not exclude
      // them when the Fancy tab is active — and vice versa. On the All tab
      // colour is not a constraint at all, because no single scale spans both.
      if (fancy) {
        if (s._col !== -1) return false;
        // Hue and intensity only bite on this tab — they have no meaning for a
        // D–K stone, and leaving them armed would empty the White tab silently.
        if (state.hues.length && state.hues.indexOf(s._hue) === -1) return false;
        if (state.intensities.length && state.intensities.indexOf(s._int) === -1) return false;
      } else if (!showAll) {
        if (s._col === -1) return false;
        if (s._col < state.colour[0] || s._col > state.colour[1]) return false;
      }

      // An UNGRADED attribute is not a bad grade. The feed leaves polish,
      // symmetry, fluorescence and occasionally clarity blank on some stones,
      // and indexOf returns -1 for those — which is below every slider's
      // minimum, so the stone silently disappeared from every search. 197 real,
      // buyable stones were invisible this way.
      //
      // The rule the cut grade already used is the right one everywhere: a
      // missing grade only excludes the stone once the shopper has actually
      // narrowed that particular slider off its full range.
      if (!inGrade(s._cla, state.clarity, CLARITY.length)) return false;
      if (!inGrade(s._cut, state.cut, CUTS.length)) return false;
      if (!inGrade(GRADE_CODES.indexOf(s.polish), state.polish, GRADE_CODES.length)) return false;
      if (!inGrade(GRADE_CODES.indexOf(s.symmetry), state.symmetry, GRADE_CODES.length)) return false;
      if (!inGrade(FLUOR.indexOf(s.fluorescence), state.fluor, FLUOR.length)) return false;

      if (s.table_pct < state.tableMin || s.table_pct > state.tableMax) return false;
      if (s.ratio < state.ratioMin || s.ratio > state.ratioMax) return false;
      if (state.lab.length && state.lab.indexOf(s.lab && s.cert ? s.lab : 'Non-certified') === -1) return false;
      return true;
    });

    var dir = state.sort.indexOf('-desc') > -1 ? -1 : 1;
    var key = state.sort.indexOf('carat') === 0 ? 'carat' : 'price';
    matches.sort(function (a, b) { return (a[key] - b[key]) * dir; });

    shown = PAGE;
    writeUrl();
    render();
  }

  /* ---------- render ---------- */
  function shapeIcon(shape) {
    var tpl = document.getElementById('ds-shape-' + shape.toLowerCase().replace(/\s+/g, '-'));
    return tpl ? tpl.innerHTML : (document.getElementById('ds-shape-other') || { innerHTML: '' }).innerHTML;
  }

  /* The grading lab, said in full beside the price rather than as a three-letter
     tag in the corner of the photograph. "IGI" means nothing to someone buying
     their first diamond; "IGI Certified" beside the price is the reassurance
     they are actually looking for. Reads s.lab, so a GIA or GSI stone names
     itself correctly. */
  var CERT_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="1.5" aria-hidden="true" width="14" height="14">' +
    '<path d="M6 3h12l4 6-10 12L2 9l4-6Z"/><path d="M2 9h20"/></svg>';

  function cardHtml(s) {
    /* Values bolded and labels left light, so the grades are scannable down a
       column of cards. Clarity and cut are both blank on part of the feed
       (1,941 of 2,385 stones carry no cut grade), and "Clarity:" followed by
       nothing reads as a broken card -- so each row is only added if it has a
       value. */
    var spec = [];
    if (s.color) spec.push('Colour: <b>' + esc(s.color) + '</b>');
    if (s.clarity) spec.push('Clarity: <b>' + esc(s.clarity) + '</b>');
    if (s.cut) spec.push('Cut: <b>' + esc(s.cut) + '</b>');
    var title = s.carat.toFixed(2) + 'ct ' + s.shape + ' ' + s.growth + ' Diamond';

    // The whole card except the button is a link to the stone's product page.
    // Inside Ring Studio it must NOT navigate — there the card is a picker for
    // the ring being built, and leaving the page would lose the setting.
    var open = !SELECT_MODE && s.handle;
    var tagOpen = open ? '<a class="ds-card__open" href="' + PRODUCT_URL + esc(s.handle) + '">' : '<div class="ds-card__open">';
    var tagClose = open ? '</a>' : '</div>';

    return '<article class="ds-card">' +
      tagOpen +
        /* No corner lab tag any more -- it said the same thing as the
           Certified line below, twice on one card. (certificate-lookup.js
           still uses .ds-card__lab, so the style stays.) */
        '<div class="ds-card__media">' + photoHtml(s, title) + '</div>' +
        '<div class="ds-card__head">' +
          '<h3 class="ds-card__title">' + esc(title) + '</h3>' +
          /* Not esc()'d as a whole: the values carry their own <b>, and each
             one was escaped individually above. */
          '<p class="ds-card__spec">' + spec.join(' · ') + '</p>' +
        '</div>' +
      tagClose +
      '<div class="ds-card__foot">' +
        '<div class="ds-card__pricerow">' +
          '<span class="ds-card__price">' + money(s.price) + '</span>' +
          (s.lab && s.cert ? '<span class="ds-card__cert">' + CERT_ICON +
            '<span>' + esc(s.lab) + ' Certified</span></span>' : '<span class="ds-card__cert">Non-certified · certification on request</span>') +
        '</div>' +
        '<button class="ds-card__btn" type="button" data-add="' + s.id + '">' +
          (SELECT_MODE ? 'Select' : (s.lab && s.cert ? 'Add to cart' : 'View diamond')) + '</button>' +
      '</div>' +
    '</article>';
  }

  /* The stone's own photograph, with the shape silhouette underneath as the
     fallback. Shopify-hosted images get a width transform so a 240 px card
     costs ~15 KB; vendor-hosted ones cannot be resized and arrive full-size,
     which is why these are lazy — only what you scroll to is fetched. */
  function photoHtml(s, title) {
    if (!s.image) return shapeIcon(s.shape);
    var src = s.image.indexOf('cdn.shopify') > -1
      ? s.image + (s.image.indexOf('?') > -1 ? '&' : '?') + 'width=480'
      : s.image;
    return '<span class="ds-card__silhouette">' + shapeIcon(s.shape) + '</span>' +
      '<img class="ds-card__photo" src="' + esc(src) + '" alt="' + esc(title) + '"' +
      ' loading="lazy" decoding="async" onerror="this.remove()">';
  }

  function listHtml(rows) {
    return '<div class="ds-list-wrap"><table class="ds-list-view">' +
      '<thead><tr><th>Shape</th><th class="ds-num">Carat</th><th>Colour</th>' +
      '<th>Clarity</th><th>Cut</th><th>Lab</th><th class="ds-num">Price</th><th></th></tr></thead><tbody>' +
      rowsHtml(rows) + '</tbody></table></div>';
  }

  function rowsHtml(rows) {
    return rows.map(function (s) {
        // Same rule as the grid: the shape cell opens the stone, except in
        // Ring Studio where navigating away would lose the ring in progress.
        var shapeCell = (!SELECT_MODE && s.handle)
          ? '<a href="' + PRODUCT_URL + esc(s.handle) + '">' + esc(s.shape) + '</a>'
          : esc(s.shape);
        return '<tr>' +
          '<td>' + shapeCell + '</td>' +
          '<td class="ds-num">' + s.carat.toFixed(2) + '</td>' +
          '<td>' + esc(s.color) + '</td>' +
          '<td>' + esc(s.clarity) + '</td>' +
          '<td>' + (s.cut ? esc(s.cut) : '—') + '</td>' +
          '<td>' + esc(s.lab && s.cert ? s.lab : 'Non-certified') + '</td>' +
          '<td class="ds-num">' + money(s.price) + '</td>' +
          '<td><button class="ds-card__btn" type="button" data-add="' + s.id + '">' +
            (SELECT_MODE ? 'Select' : (s.lab && s.cert ? 'Add' : 'View diamond')) + '</button></td>' +
        '</tr>';
      }).join('');
  }

  /* Plain-English summary of what is currently being asked for. Used by the
     sourcing fallback in Ring Studio, so a visitor who finds nothing does not
     have to retype the search they just built. Kept here rather than in the
     studio because only this file knows what the filter state means. */
  /* "a, b or c" — joining four shapes with " or " read as a shouted list. */
  function listPhrase(a) {
    if (a.length < 2) return a.join('');
    return a.slice(0, -1).join(', ') + ' or ' + a[a.length - 1];
  }

  function describeQuery() {
    var bits = [];
    if (state.shapes.length) bits.push(listPhrase(state.shapes));
    /* Ranges are seeded to the full spread of the catalogue, so printing them
       unconditionally put "$0–261000" in every request — noise that makes the
       useful parts harder to read. Only mention a range once it is narrower
       than everything we hold. */
    if (state.caratMin > bounds.carat[0] || state.caratMax < bounds.carat[1]) {
      bits.push(state.caratMin + '–' + state.caratMax + 'ct');
    }
    if (state.colourTab === 'fancy') {
      if (state.intensities.length) bits.push(state.intensities.join('/'));
      bits.push(state.hues.length ? listPhrase(state.hues) : 'fancy colour');
    } else if (state.colour[0] > 0 || state.colour[1] < COLOURS.length - 1) {
      bits.push('colour ' + COLOURS[state.colour[1]] + '–' + COLOURS[state.colour[0]]);
    }
    if (state.clarity[0] > 0 || state.clarity[1] < CLARITY.length - 1) {
      bits.push('clarity ' + CLARITY[state.clarity[1]] + '–' + CLARITY[state.clarity[0]]);
    }
    if (state.priceMin > bounds.price[0] || state.priceMax < bounds.price[1]) {
      bits.push('$' + Math.round(state.priceMin).toLocaleString('en-US') + '–$' +
                Math.round(state.priceMax).toLocaleString('en-US'));
    }
    if (state.growth) bits.push(state.growth);
    return bits.join(', ');
  }

  var moreObserver = null;
  var moreFrame = 0;

  function stopAutoLoad() {
    if (moreObserver) moreObserver.disconnect();
    if (moreFrame) cancelAnimationFrame(moreFrame);
    moreFrame = 0;
  }

  function queueAutoLoad() {
    if (moreFrame) return;
    moreFrame = requestAnimationFrame(function () {
      moreFrame = 0;
      var marker = $('[data-auto-more]');
      if (!marker || !marker.getClientRects().length || document.hidden) return;
      var box = marker.getBoundingClientRect();
      if (box.top <= window.innerHeight + 300 && box.bottom >= 0) appendMore();
    });
  }

  function watchMore() {
    if (moreObserver) moreObserver.disconnect();
    var marker = $('[data-auto-more]');
    if (!marker) return;
    if ('IntersectionObserver' in window) {
      if (!moreObserver) moreObserver = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) queueAutoLoad();
      }, { rootMargin: '300px 0px' });
      moreObserver.observe(marker);
    }
    queueAutoLoad();
  }

  function appendMore() {
    var marker = $('[data-auto-more]');
    if (!marker || shown >= matches.length) return;
    var next = matches.slice(shown, shown + PAGE);
    var target = state.view === 'list' ? $('.ds-list-view tbody') : $('.ds-grid-view');
    if (!target) return;
    var scrollTop = window.scrollY;
    target.insertAdjacentHTML('beforeend', state.view === 'list' ? rowsHtml(next) : next.map(cardHtml).join(''));
    if (window.scrollY !== scrollTop) window.scrollTo(0, scrollTop);
    shown += next.length;
    marker.textContent = 'Showing ' + shown.toLocaleString('en-US') + ' of ' + matches.length.toLocaleString('en-US') + ' diamonds';
    if (shown >= matches.length) {
      marker.removeAttribute('data-auto-more');
      if (moreObserver) moreObserver.disconnect();
    } else watchMore();
  }

  window.addEventListener('scroll', queueAutoLoad, { passive: true });
  window.addEventListener('resize', queueAutoLoad, { passive: true });
  root.addEventListener('focusin', queueAutoLoad);

  function render() {
    stopAutoLoad();
    var out = $('[data-results]');
    var n = matches.length;

    $('[data-count]').innerHTML = n
      ? 'Results — <b>' + n.toLocaleString('en-US') + '</b> ' + (n === 1 ? 'Diamond' : 'Diamonds')
      : 'No diamonds match';

    /* Announce the result size so an outside caller can offer to source what
       the catalogue does not hold. An event, not a fork — the studio never
       reaches into this engine's state. */
    var results = { count: n, query: describeQuery() };
    /* Also stashed, because this engine renders as soon as its own index loads
       while Ring Studio boots behind two fetches — so the first event fires
       before the studio's listener exists. A late listener reads this instead
       of waiting for a change that may never come. */
    root.lastResults = results;
    root.dispatchEvent(new CustomEvent('diamond:results', { detail: results, bubbles: true }));

    if (!n) {
      // "Try widening the filters" is a lie when the catalogue holds none of
      // this growth type at all — the Natural Diamonds page is pinned to a
      // growth the feed never supplies, and no amount of filter-clearing will
      // ever produce a stone. Say which it is.
      var noneOfType = state.growth && !stones.some(function (s) {
        return s.growth === state.growth;
      });

      out.innerHTML = noneOfType
        ? '<div class="ds-empty">' +
            '<h3>No ' + esc(state.growth.toLowerCase()) + ' diamonds in stock right now</h3>' +
            '<p class="lede center-lede">Our current inventory does not include any ' +
              esc(state.growth.toLowerCase()) + ' stones. Contact us with your preferred shape, carat weight and budget to discuss availability.</p>' +
            '<p style="margin-top:var(--s-5)"><a class="btn btn--primary" href="/pages/contact">Enquire about a diamond</a></p>' +
            '<p><a href="/pages/all-diamonds">Browse available diamonds</a></p>' +
          '</div>'
        : '<div class="ds-empty">' +
            '<h3>No diamonds match these filters</h3>' +
            '<p class="lede center-lede">Try widening the carat or price range, or clearing a filter.</p>' +
            '<p style="margin-top:var(--s-5)"><button class="btn btn--ghost" type="button" data-reset>Clear all filters</button></p>' +
          '</div>';
      return;
    }

    var page = matches.slice(0, shown);
    out.innerHTML = state.view === 'list'
      ? listHtml(page)
      : '<div class="ds-grid-view">' + page.map(cardHtml).join('') + '</div>';

    if (shown < n) {
      if (MANUAL_MORE) {
        out.insertAdjacentHTML('beforeend',
          '<div class="ds-more-wrap"><button class="btn btn--ghost" type="button" data-loadmore>' +
          'Load more — showing ' + shown.toLocaleString('en-US') + ' of ' + n.toLocaleString('en-US') +
          '</button></div>');
      } else {
        out.insertAdjacentHTML('beforeend',
          '<div class="ds-more-wrap" data-auto-more role="status" aria-live="polite">' +
          'Showing ' + shown.toLocaleString('en-US') + ' of ' + n.toLocaleString('en-US') + ' diamonds — scroll for more</div>');
        watchMore();
      }
    }

  }

  /* ---------- controls ---------- */
  /* An ordered scale rendered as a real slider: rail, fill, two draggable
     knobs, labels beneath. Stop i sits at the centre of its column. */
  function stopPct(i, n) { return ((i + 0.5) * 100) / n; }

  function scale(el, values, key) {
    var n = values.length;
    el.innerHTML =
      '<div class="ds-scale__track">' +
        '<div class="ds-scale__rail"></div>' +
        '<div class="ds-scale__fill"></div>' +
        '<span class="ds-scale__knob" data-k="0" tabindex="0" role="slider" ' +
          'aria-label="Minimum ' + key + '"></span>' +
        '<span class="ds-scale__knob" data-k="1" tabindex="0" role="slider" ' +
          'aria-label="Maximum ' + key + '"></span>' +
      '</div>' +
      '<div class="ds-scale__stops" style="grid-template-columns:repeat(' + n + ',1fr)">' +
        values.map(function (v, i) {
          return '<button type="button" data-i="' + i + '">' + esc(v) + '</button>';
        }).join('') +
      '</div>';

    var rail = el.querySelector('.ds-scale__rail');
    rail.style.left = stopPct(0, n) + '%';
    rail.style.right = (100 - stopPct(n - 1, n)) + '%';

    var track = el.querySelector('.ds-scale__track');
    for (var t = 0; t < n; t++) {
      var tick = document.createElement('span');
      tick.className = 'ds-scale__tick';
      tick.style.left = stopPct(t, n) + '%';
      track.insertBefore(tick, track.querySelector('.ds-scale__knob'));
    }

    el.querySelector('.ds-scale__stops').addEventListener('click', function (e) {
      var b = e.target.closest('button'); if (!b) return;
      var i = +b.getAttribute('data-i');
      var r = state[key];
      // From a full range a click means "just this grade" — otherwise clicking
      // an end value would silently do nothing and read as broken. Once
      // narrowed, a click moves whichever knob is nearer.
      if (r[0] === 0 && r[1] === n - 1) { r[0] = i; r[1] = i; }
      else if (i < r[0]) r[0] = i;
      else if (i > r[1]) r[1] = i;
      else if (i - r[0] <= r[1] - i) r[0] = i;
      else r[1] = i;
      paintScale(el, key);
      apply();
    });

    // Knobs must actually drag — a control that looks like a slider but only
    // responds to clicks reads as broken.
    el.querySelectorAll('.ds-scale__knob').forEach(function (knob) {
      var which = +knob.getAttribute('data-k');

      function toIndex(clientX) {
        var box = el.querySelector('.ds-scale__track').getBoundingClientRect();
        var pct = ((clientX - box.left) / box.width) * 100;
        var i = Math.round((pct / 100) * n - 0.5);
        return Math.max(0, Math.min(n - 1, i));
      }
      function move(ev) {
        var i = toIndex(ev.clientX);
        var r = state[key];
        if (which === 0) r[0] = Math.min(i, r[1]); else r[1] = Math.max(i, r[0]);
        paintScale(el, key);
        apply();
      }
      knob.addEventListener('pointerdown', function (ev) {
        ev.preventDefault();
        knob.setPointerCapture(ev.pointerId);
        knob.addEventListener('pointermove', move);
        knob.addEventListener('pointerup', function up() {
          knob.removeEventListener('pointermove', move);
          knob.removeEventListener('pointerup', up);
        });
      });
      knob.addEventListener('keydown', function (ev) {
        var d = ev.key === 'ArrowRight' ? 1 : ev.key === 'ArrowLeft' ? -1 : 0;
        if (!d) return;
        ev.preventDefault();
        var r = state[key];
        if (which === 0) r[0] = Math.max(0, Math.min(r[0] + d, r[1]));
        else r[1] = Math.min(n - 1, Math.max(r[1] + d, r[0]));
        paintScale(el, key);
        apply();
      });
    });

    paintScale(el, key);
  }

  function paintScale(el, key) {
    var stops = el.querySelectorAll('.ds-scale__stops button');
    var n = stops.length;
    var r = state[key];
    var lo = stopPct(r[0], n), hi = stopPct(r[1], n);

    el.querySelector('.ds-scale__fill').style.left = lo + '%';
    el.querySelector('.ds-scale__fill').style.width = (hi - lo) + '%';
    var knobs = el.querySelectorAll('.ds-scale__knob');
    knobs[0].style.left = lo + '%';
    knobs[1].style.left = hi + '%';
    knobs[0].setAttribute('aria-valuetext', stops[r[0]].textContent);
    knobs[1].setAttribute('aria-valuetext', stops[r[1]].textContent);

    Array.prototype.forEach.call(stops, function (b, i) {
      b.setAttribute('data-in', i >= r[0] && i <= r[1] ? 'true' : 'false');
    });

    var out = el.parentNode.querySelector('.ds-group__value[data-readout]');
    if (out && key === 'colour' && state.colourTab !== 'white') {
      out.textContent = state.colourTab === 'all' ? 'Every colour' : fancyReadout();
    } else if (out) {
      out.textContent = r[0] === r[1]
        ? stops[r[0]].textContent
        : stops[r[0]].textContent + ' \u2013 ' + stops[r[1]].textContent;
    }
  }

  function dualRange(wrap, min, max, step, keyMin, keyMax, fmt) {
    var lo = wrap.querySelector('[data-lo]');
    var hi = wrap.querySelector('[data-hi]');
    var nLo = wrap.querySelector('[data-nlo]');
    var nHi = wrap.querySelector('[data-nhi]');
    var fill = wrap.querySelector('.ds-range__fill');

    [lo, hi].forEach(function (i) { i.min = min; i.max = max; i.step = step; });
    lo.value = state[keyMin]; hi.value = state[keyMax];

    function paint() {
      var a = +lo.value, b = +hi.value;
      var l = ((a - min) / (max - min)) * 100;
      var r = ((b - min) / (max - min)) * 100;
      fill.style.left = l + '%';
      fill.style.width = Math.max(0, r - l) + '%';
      nLo.value = fmt ? fmt(a) : a;
      nHi.value = fmt ? fmt(b) : b;
      var out = wrap.parentNode.querySelector('.ds-group__value[data-readout]');
      if (out) out.textContent = (fmt ? fmt(a) : a) + ' \u2013 ' + (fmt ? fmt(b) : b);
    }

    function commit() {
      var a = Math.min(+lo.value, +hi.value);
      var b = Math.max(+lo.value, +hi.value);
      lo.value = a; hi.value = b;
      state[keyMin] = a; state[keyMax] = b;
      paint(); apply();
    }

    lo.addEventListener('input', function () {
      if (+lo.value > +hi.value) lo.value = hi.value;
      paint();
    });
    hi.addEventListener('input', function () {
      if (+hi.value < +lo.value) hi.value = lo.value;
      paint();
    });
    lo.addEventListener('change', commit);
    hi.addEventListener('change', commit);

    [[nLo, keyMin, lo], [nHi, keyMax, hi]].forEach(function (t) {
      t[0].addEventListener('change', function () {
        var v = parseFloat(String(t[0].value).replace(/[^0-9.]/g, ''));
        if (isNaN(v)) { paint(); return; }
        t[2].value = Math.min(max, Math.max(min, v));
        commit();
      });
    });

    paint();
  }

  function chips(el, key, values, after) {
    el.innerHTML = values.map(function (v) {
      var on = state[key].indexOf(v) > -1;      // the URL may have pre-selected it
      return '<button type="button" aria-pressed="' + on + '" data-v="' + esc(v) + '">' +
             esc(v) + '</button>';
    }).join('');
    el.addEventListener('click', function (e) {
      var b = e.target.closest('button'); if (!b) return;
      var v = b.getAttribute('data-v');
      var arr = state[key];
      var i = arr.indexOf(v);
      if (i > -1) arr.splice(i, 1); else arr.push(v);
      b.setAttribute('aria-pressed', i > -1 ? 'false' : 'true');
      if (after) after();
      apply();
    });
  }

  /* ---------- fancy colour ---------- */

  /* Offer only what is in stock. The feed is a live inventory: it carries two
     Fancy Reds today and may carry none next week, and a chip that always
     returns nothing is worse than no chip. Anything outside the canonical order
     still gets a chip, appended, rather than being silently unreachable. */
  function presentValues(prop, order) {
    var seen = {};
    stones.forEach(function (s) {
      if (s._col === -1 && s[prop]) seen[s[prop]] = true;
    });
    var known = order.filter(function (v) { return seen[v]; });
    var extra = Object.keys(seen).filter(function (v) { return order.indexOf(v) === -1; }).sort();
    return known.concat(extra);
  }

  function fancyReadout() {
    var picked = state.hues.concat(state.intensities);
    if (!picked.length) return 'All fancy colours';
    if (picked.length <= 2) return picked.join(', ');
    return picked.length + ' selected';
  }

  function paintFancy() {
    var box = $('[data-fancy]');
    if (!box) return;
    box.hidden = state.colourTab !== 'fancy';
    var out = $('[data-scale-colour]').parentNode.querySelector('.ds-group__value[data-readout]');
    if (out && state.colourTab === 'fancy') out.textContent = fancyReadout();
  }

  function paintShapes() {
    var out = $('[data-shape-readout]');
    if (!out) return;
    var n = state.shapes.length;
    out.textContent = n === 0 ? 'All shapes'
      : n <= 2 ? state.shapes.join(', ')
      : n + ' selected';
  }

  function buildControls() {
    // Shapes
    $$('[data-shape]').forEach(function (b) {
      var v = b.getAttribute('data-shape');
      b.setAttribute('aria-pressed', state.shapes.indexOf(v) > -1 ? 'true' : 'false');
      b.addEventListener('click', function () {
        var i = state.shapes.indexOf(v);
        if (i > -1) state.shapes.splice(i, 1); else state.shapes.push(v);
        b.setAttribute('aria-pressed', i > -1 ? 'false' : 'true');
        paintShapes();
        apply();
      });
    });
    paintShapes();

    var moreBtn = $('[data-more]');
    if (moreBtn) moreBtn.addEventListener('click', function () {
      var extra = $('.ds-shapes--extra');
      var open = extra.getAttribute('data-open') === 'true';
      extra.setAttribute('data-open', String(!open));
      moreBtn.textContent = open ? 'More shapes' : 'Fewer shapes';
    });

    // Scales
    scale($('[data-scale-colour]'), COLOURS, 'colour');
    scale($('[data-scale-clarity]'), CLARITY, 'clarity');
    scale($('[data-scale-cut]'), CUTS, 'cut');

    // Colour tabs — reflect the page's starting tab before wiring clicks.
    $$('[data-ctab]').forEach(function (b) {
      b.setAttribute('aria-selected', String(b.getAttribute('data-ctab') === state.colourTab));
    });
    $('[data-scale-colour]').style.display = state.colourTab === 'white' ? '' : 'none';

    // Fancy hue + intensity, built from the stones actually indexed.
    chips($('[data-chips-hue]'), 'hues', presentValues('_hue', HUE_ORDER), paintFancy);
    chips($('[data-chips-intensity]'), 'intensities', presentValues('_int', INTENSITIES), paintFancy);
    paintFancy();

    $$('[data-ctab]').forEach(function (b) {
      b.addEventListener('click', function () {
        state.colourTab = b.getAttribute('data-ctab');
        $$('[data-ctab]').forEach(function (o) {
          o.setAttribute('aria-selected', String(o === b));
        });
        $('[data-scale-colour]').style.display = state.colourTab === 'white' ? '' : 'none';
        paintScale($('[data-scale-colour]'), 'colour');
        paintFancy();
        apply();
      });
    });

    // Ranges
    dualRange($('[data-range-carat]'), bounds.carat[0], bounds.carat[1], 0.01,
      'caratMin', 'caratMax', function (v) { return v.toFixed(2); });
    dualRange($('[data-range-price]'), bounds.price[0], bounds.price[1], 50,
      'priceMin', 'priceMax', function (v) { return money(v); });

    // Advanced
    scale($('[data-scale-polish]'), GRADE_LABELS, 'polish');
    scale($('[data-scale-symmetry]'), GRADE_LABELS, 'symmetry');
    scale($('[data-scale-fluor]'), FLUOR, 'fluor');
    dualRange($('[data-range-table]'), bounds.table[0], bounds.table[1], 0.5,
      'tableMin', 'tableMax', function (v) { return v.toFixed(1) + '%'; });
    dualRange($('[data-range-ratio]'), bounds.ratio[0], bounds.ratio[1], 0.01,
      'ratioMin', 'ratioMax', function (v) { return v.toFixed(2); });
    chips($('[data-chips-lab]'), 'lab', ['IGI', 'GIA', 'Non-certified']);

    var advBtn = $('[data-adv]');
    advBtn.addEventListener('click', function () {
      var box = $('.ds-adv');
      var open = box.getAttribute('data-open') === 'true';
      box.setAttribute('data-open', String(!open));
      advBtn.textContent = open ? 'Advanced filters' : 'Hide advanced filters';
    });

    // Toolbar
    $('[data-sort]').value = state.sort;
    $('[data-sort]').addEventListener('change', function (e) {
      state.sort = e.target.value; apply();
    });
    $$('[data-view]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-view') === state.view));
      b.addEventListener('click', function () {
        state.view = b.getAttribute('data-view');
        $$('[data-view]').forEach(function (o) {
          o.setAttribute('aria-pressed', String(o === b));
        });
        render();
      });
    });

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-loadmore]')) { shown += PAGE; render(); }
      if (e.target.closest('[data-reset]')) reset();

      var add = e.target.closest('[data-add]');

      /* In Ring Studio the WHOLE card picks the stone, not just its button —
         a card that looks clickable and only responds on a small button reads
         as broken. Outside the studio the card is a shop listing whose body is
         a link to the product page, so only the button may buy. */
      if (!add && SELECT_MODE) {
        var card = e.target.closest('.ds-card, .ds-list-view tbody tr');
        if (card) add = card.querySelector('[data-add]');
      }
      if (!add) return;

      var id = +add.getAttribute('data-add');
      var stone = stones.filter(function (s) { return s.id === id; })[0];
      if (!stone) return;

      // Inside Ring Studio the card is a picker: hand the stone back and let
      // the studio add it alongside its setting as one ring. Everywhere else
      // the card is a shop listing, and the button has to actually buy.
      if (SELECT_MODE) {
        root.dispatchEvent(new CustomEvent('diamond:select', { detail: stone, bubbles: true }));
        return;
      }
      if (!(stone.lab && stone.cert)) { window.location.href = PRODUCT_URL + encodeURIComponent(stone.handle); return; }
      addStone(add, stone);
    });
  }

  /* Buy a single stone. Mirrors ring-studio.js's guard: an index row without a
     variant id means the stones are not real Shopify products yet, and posting
     the row id would 404 or add someone else's product. Fail visibly instead. */
  function addStone(btn, stone) {
    if (!stone.variant_id) {
      btn.textContent = 'Unavailable';
      btn.disabled = true;
      return;
    }

    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: stone.variant_id,
          quantity: 1,
          properties: {
            'Certification status': stone.lab && stone.cert ? stone.lab + ' ' + stone.cert : 'Non-certified',
            'Stone no.': stone.stone_no
          }
        }]
      })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || 'Sold out'); });
      window.location.href = window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root + 'cart' : '/cart';
    }).catch(function (err) {
      btn.textContent = err.message.slice(0, 24);
      setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 2500);
    });
  }

  function reset() {
    state.shapes = [];
    state.caratMin = bounds.carat[0]; state.caratMax = bounds.carat[1];
    state.priceMin = bounds.price[0]; state.priceMax = bounds.price[1];
    state.colour = [0, 7]; state.clarity = [0, 7]; state.cut = [0, 3];
    state.hues = []; state.intensities = [];
    state.polish = [0, 2]; state.symmetry = [0, 2]; state.fluor = [0, 3];
    state.tableMin = bounds.table[0]; state.tableMax = bounds.table[1];
    state.ratioMin = bounds.ratio[0]; state.ratioMax = bounds.ratio[1];
    state.lab = [];
    state.colourTab = root.getAttribute('data-colour-tab') || 'white';
    $$('[data-shape]').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
    paintShapes();
    $$('.ds-chips button').forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
    $('[data-scale-colour]').style.display = '';
    $$('[data-ctab]').forEach(function (o) {
      o.setAttribute('aria-selected', String(o.getAttribute('data-ctab') === state.colourTab));
    });
    $('[data-scale-colour]').style.display = state.colourTab === 'white' ? '' : 'none';
    paintFancy();
    buildRangesAgain();
    paintScale($('[data-scale-colour]'), 'colour');
    paintScale($('[data-scale-clarity]'), 'clarity');
    paintScale($('[data-scale-cut]'), 'cut');
    paintScale($('[data-scale-polish]'), 'polish');
    paintScale($('[data-scale-symmetry]'), 'symmetry');
    paintScale($('[data-scale-fluor]'), 'fluor');
    apply();
  }

  function buildRangesAgain() {
    [['[data-range-carat]', 'caratMin', 'caratMax'], ['[data-range-price]', 'priceMin', 'priceMax'],
     ['[data-range-table]', 'tableMin', 'tableMax'], ['[data-range-ratio]', 'ratioMin', 'ratioMax']]
      .forEach(function (t) {
        var w = $(t[0]);
        w.querySelector('[data-lo]').value = state[t[1]];
        w.querySelector('[data-hi]').value = state[t[2]];
        w.querySelector('[data-lo]').dispatchEvent(new Event('input'));
      });
  }

  load();
})();
